import React, { useState } from "react";
import { Radio, Input, Button, Space, Modal, Alert, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { actionReport, REPORT_ACTIONS } from "../../Services/moderationApi";

const DESTRUCTIVE = new Set([
  "suspend_3",
  "suspend_7",
  "suspend_permanent",
]);

/**
 * Action selector for a single report. Renders the six admin actions,
 * an optional note, and a confirmation dialog before anything
 * irreversible is sent.
 *
 * Props:
 *   report   - the report detail object (needs `id`/`_key` and `status`)
 *   onDone   - called after a successful action so the parent can refresh
 */
const ReportActionPanel = ({ report, onDone }) => {
  const [choice, setChoice] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reportId = report?.id || report?._key;
  const alreadyClosed =
    report?.status === "actioned" || report?.status === "dismissed";

  const submit = async () => {
    if (!choice || !reportId) return;
    setSubmitting(true);
    try {
      const resp = await actionReport(reportId, choice, note);
      if (resp?.data?.error || resp?.status >= 400) {
        message.error(resp?.data?.error || "Action failed");
      } else {
        message.success(resp?.data?.message || "Action applied");
        onDone && onDone();
      }
    } catch (err) {
      message.error(
        err?.response?.data?.error || "Action failed, please try again"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const confirmAndSubmit = () => {
    if (!choice) {
      message.warning("Pick an action first");
      return;
    }
    const label =
      REPORT_ACTIONS.find((a) => a.value === choice)?.label || choice;
    Modal.confirm({
      title: `${label}?`,
      icon: <ExclamationCircleOutlined />,
      content: DESTRUCTIVE.has(choice)
        ? "This suspends the reported user and notifies them by email. Suspensions follow the 3-strike ladder (3 days → 7 days → permanent) regardless of the option picked here."
        : "This will be recorded against the report and the affected user will be notified.",
      okText: "Confirm",
      okButtonProps: { danger: DESTRUCTIVE.has(choice) },
      cancelText: "Cancel",
      onOk: submit,
    });
  };

  if (alreadyClosed) {
    return (
      <Alert
        type="info"
        showIcon
        message="This report has already been actioned."
        description={
          report?.action_taken
            ? `Action taken: ${report.action_taken}`
            : undefined
        }
      />
    );
  }

  return (
    <div>
      <h4 style={{ marginBottom: 8 }}>Take action</h4>
      <Radio.Group
        onChange={(e) => setChoice(e.target.value)}
        value={choice}
        style={{ display: "flex", flexDirection: "column", gap: 6 }}
      >
        {REPORT_ACTIONS.map((a) => (
          <Radio key={a.value} value={a.value}>
            {a.label}
          </Radio>
        ))}
      </Radio.Group>

      <Input.TextArea
        rows={3}
        placeholder="Internal note / reason (optional, shown in the user's notification for warnings & suspensions)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        style={{ marginTop: 12 }}
        maxLength={500}
      />

      <Space style={{ marginTop: 12 }}>
        <Button
          type="primary"
          loading={submitting}
          disabled={!choice}
          onClick={confirmAndSubmit}
        >
          Apply
        </Button>
      </Space>
    </div>
  );
};

export default ReportActionPanel;
